import React from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Code2,
  BarChart3,
  Database,
  PieChart,
  Sheet,
  Bot,
  RefreshCw,
  Activity,
  CheckCircle2,
} from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

/* Network geometry — 7 nodes, Gen AI on top, flat base (percent coords) */
const CENTER = { x: 50, y: 50 };
const R = 36;
const NODES = [
  { label: "Gen AI", icon: Sparkles, angle: -90, accent: true },
  { label: "Python", icon: Code2, angle: -38.57, accent: false },
  { label: "Power BI", icon: BarChart3, angle: 12.86, accent: false },
  { label: "Agentic AI", icon: Bot, angle: 64.29, accent: true },
  { label: "SQL", icon: Database, angle: 115.71, accent: true },
  { label: "Tableau", icon: PieChart, angle: 167.14, accent: false },
  { label: "Excel", icon: Sheet, angle: 218.57, accent: false },
].map((n) => ({
  ...n,
  x: CENTER.x + R * Math.cos((n.angle * Math.PI) / 180),
  y: CENTER.y + R * Math.sin((n.angle * Math.PI) / 180),
}));

const EDGES = NODES.map((n, i) => [n, NODES[(i + 1) % NODES.length]]);

const BARS = [0.55, 0.85, 0.45, 0.95, 0.65, 0.8, 0.5];
const BUILD_ITEMS = ["Sales Dashboard", "KPI Report", "Analyst AI Agent", "Forecast Model"];
const LOOP_STEPS = ["Collect", "Analyze", "Visualize", "Act"];

const nodePop = {
  hidden: { opacity: 0, scale: 0.6 },
  show: (i) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.55, ease: EASE, delay: 1.0 + i * 0.09 },
  }),
};

const widgetIn = {
  hidden: { opacity: 0, y: 14 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: EASE, delay: 1.5 + i * 0.15 },
  }),
};

const cardClass =
  "rounded-xl border border-[#293891]/10 bg-white/95 shadow-[0_18px_40px_-18px_rgba(41,56,145,0.3)] backdrop-blur-md";

export default function NetworkVisual() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 34, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 1.1, ease: EASE, delay: 0.55 }}
      className="nx-panel relative aspect-square w-full"
      data-testid="hero-network-visual"
    >
      {/* very soft center glow — blends into the hero background */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(41,56,145,0.05), transparent 62%)" }} />

      {/* radar sweep behind the constellation */}
      <div
        className="absolute left-1/2 top-1/2 aspect-square w-[72%] rounded-full"
        style={{
          transform: "translate(-50%,-50%)",
          overflow: "hidden",
        }}
      >
        <div
          className="h-full w-full rounded-full"
          style={{
            background: "conic-gradient(from 0deg, rgba(41,56,145,0.10), transparent 22%, transparent)",
            animation: "nxSpin 9s linear infinite",
          }}
        />
      </div>

      {/* large boundary ring + instrument tick ring */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, ease: EASE, delay: 0.75 }}
        className="absolute left-1/2 top-1/2 aspect-square w-[94%]"
        style={{ x: "-50%", y: "-50%" }}
      >
        <svg viewBox="0 0 100 100" className="h-full w-full">
          <circle cx="50" cy="50" r="49" fill="none" stroke="rgba(41,56,145,0.09)" strokeWidth="0.4" />
          <circle
            cx="50" cy="50" r="49" fill="none"
            stroke="rgba(41,56,145,0.18)" strokeWidth="1.4"
            strokeDasharray="0.35 6.15"
          />
        </svg>
      </motion.div>
      <div
        className="absolute left-1/2 top-1/2 aspect-square w-[76%]"
        style={{ transform: "translate(-50%,-50%)" }}
      >
        <div
          className="h-full w-full rounded-full border border-dashed border-[#293891]/[0.13]"
          style={{ animation: "nxSpin 60s linear infinite" }}
        />
      </div>

      {/* connecting lines */}
      <motion.svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.9 }}
      >
        {EDGES.map(([a, b], i) => (
          <line
            key={`e-${i}`}
            x1={a.x} y1={a.y} x2={b.x} y2={b.y}
            stroke="rgba(41,56,145,0.15)"
            strokeWidth="0.28"
            strokeDasharray="1.6 1.4"
            className="nx-dash"
          />
        ))}
        {NODES.map((n, i) => (
          <line
            key={`s-${i}`}
            x1={n.x} y1={n.y} x2={CENTER.x} y2={CENTER.y}
            stroke={n.accent ? "rgba(230,51,41,0.4)" : "rgba(41,56,145,0.26)"}
            strokeWidth="0.3"
            strokeDasharray="1.8 1.4"
            className="nx-dash"
          />
        ))}
      </motion.svg>

      {/* data packets flowing node → core */}
      {NODES.map((n, i) =>
        [0, 1].map((k) => (
          <span
            key={`p-${i}-${k}`}
            className="nx-packet"
            style={{
              "--sx": `${n.x}%`,
              "--sy": `${n.y}%`,
              "--cx": `${CENTER.x}%`,
              "--cy": `${CENTER.y}%`,
              backgroundColor: n.accent ? "#E63329" : "#293891",
              boxShadow: n.accent
                ? "0 0 6px 1px rgba(230,51,41,0.4)"
                : "0 0 6px 1px rgba(41,56,145,0.35)",
              animationDuration: `${2.6 + i * 0.22}s`,
              animationDelay: `${k * 1.3 + i * 0.35}s`,
            }}
          />
        ))
      )}

      {/* central AI core — centered via framer x/y so scale never breaks centering */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, ease: EASE, delay: 0.8 }}
        className="absolute aspect-square w-[18%]"
        style={{ left: `${CENTER.x}%`, top: `${CENTER.y}%`, x: "-50%", y: "-50%" }}
      >
        <div className="nx-core-glow absolute -inset-[55%] rounded-full" />
        <div className="absolute -inset-[26%]">
          <div
            className="h-full w-full rounded-full border border-dashed border-[#293891]/25"
            style={{ animation: "nxSpin 22s linear infinite" }}
          />
        </div>
        <div className="absolute inset-0 rounded-full bg-white shadow-[0_20px_46px_-14px_rgba(41,56,145,0.45)]" />
        <div className="absolute inset-[9%] flex flex-col items-center justify-center rounded-full bg-gradient-to-br from-[#2E3FA0] to-[#1A2560]">
          <span className="font-display text-[clamp(1rem,2.8vw,1.6rem)] font-semibold leading-none tracking-wide text-white">
            AI
          </span>
          <span className="mt-1 text-[6.5px] font-medium uppercase tracking-[0.35em] text-white/55">
            Core
          </span>
        </div>
      </motion.div>

      {/* skill node chips — framer x/y keeps them centered on their orbit points */}
      {NODES.map((n, i) => {
        const Icon = n.icon;
        return (
          <motion.div
            key={n.label}
            custom={i}
            variants={nodePop}
            initial="hidden"
            animate="show"
            className="absolute"
            style={{ left: `${n.x}%`, top: `${n.y}%`, x: "-50%", y: "-50%" }}
          >
            <div
              className={`nx-bob flex items-center gap-1.5 rounded-full border bg-white py-1 pl-1 pr-2 shadow-[0_10px_26px_-12px_rgba(41,56,145,0.4)] min-[480px]:py-1.5 min-[480px]:pl-1.5 min-[480px]:pr-3 lg:py-2 lg:pr-3.5 ${
                n.accent ? "border-[#E63329]/40" : "border-[#293891]/20"
              }`}
              style={{ animationDelay: `${i * 0.45}s` }}
            >
              <span
                className={`flex h-[16px] w-[16px] items-center justify-center rounded-full min-[480px]:h-5 min-[480px]:w-5 lg:h-6 lg:w-6 ${
                  n.accent ? "bg-[#E63329]" : "bg-[#293891]"
                }`}
              >
                <Icon size={10} color="#ffffff" strokeWidth={2.4} className="lg:hidden" />
                <Icon size={13} color="#ffffff" strokeWidth={2.4} className="hidden lg:block" />
              </span>
              <span className="whitespace-nowrap text-[9px] font-semibold tracking-wide text-[#293891] min-[480px]:text-[10px] lg:text-xs">
                {n.label}
              </span>
            </div>
          </motion.div>
        );
      })}

      {/* widget — analyst loop (top-left) */}
      <div className="absolute left-[1%] top-[3%] z-10 origin-top-left scale-[0.68] min-[480px]:scale-90 lg:scale-100">
        <motion.div custom={0} variants={widgetIn} initial="hidden" animate="show" className={`px-3.5 py-3 ${cardClass}`}>
          <div className="mb-2 flex items-center gap-1.5 text-[8px] font-semibold uppercase tracking-[0.22em] text-[#566076]">
            <RefreshCw size={9} className="text-[#293891]/60" />
            Analyst Loop
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {LOOP_STEPS.map((s, i) => (
              <span
                key={s}
                className={`rounded-md px-2.5 py-1 text-center text-[8px] font-semibold tracking-wide ${
                  i === LOOP_STEPS.length - 1
                    ? "bg-[#E63329] text-white shadow-[0_4px_12px_rgba(230,51,41,0.35)]"
                    : "bg-[#293891]/[0.07] text-[#293891]"
                }`}
              >
                {s}
              </span>
            ))}
          </div>
        </motion.div>
      </div>

      {/* widget — live data bars (top-right) */}
      <div className="absolute right-[1%] top-[3%] z-10 origin-top-right scale-[0.68] min-[480px]:scale-90 lg:scale-100">
        <motion.div custom={1} variants={widgetIn} initial="hidden" animate="show" className={`px-3.5 pb-2.5 pt-2 ${cardClass}`}>
          <div className="mb-1.5 flex items-center gap-1.5 text-[8px] font-semibold uppercase tracking-[0.22em] text-[#566076]">
            <Activity size={9} className="text-[#E63329]" />
            Live Data
            <span className="ml-auto h-1.5 w-1.5 animate-pulse rounded-full bg-[#E63329]" />
          </div>
          <div className="flex h-[46px] items-end gap-1.5">
            {BARS.map((h, i) => (
              <span
                key={i}
                className="nx-bar block w-[7px] rounded-[2px]"
                style={{
                  height: `${h * 100}%`,
                  backgroundColor: i % 3 === 0 ? "#E63329" : "#293891",
                  animationDelay: `${i * 0.18}s`,
                }}
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* widget — you build (bottom-right) */}
      <div className="absolute bottom-[3%] right-[1%] z-10 origin-bottom-right scale-[0.68] min-[480px]:scale-90 lg:scale-100">
        <motion.div
          custom={2}
          variants={widgetIn}
          initial="hidden"
          animate="show"
          className="rounded-xl bg-gradient-to-br from-[#293891] to-[#1A2560] px-3.5 py-3 shadow-[0_20px_44px_-16px_rgba(26,37,96,0.5)]"
        >
          <div className="mb-2 text-[7px] font-semibold uppercase tracking-[0.24em] text-white/45">
            You Build
          </div>
          <div className="flex flex-col gap-1.5">
            {BUILD_ITEMS.map((b) => (
              <div key={b} className="flex items-center gap-1.5">
                <CheckCircle2 size={10} className="shrink-0 text-[#FF5A4D]" />
                <span className="whitespace-nowrap text-[9px] font-medium text-white/90">{b}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
